import { createAsyncThunk } from '@reduxjs/toolkit';

import { IQuestion, IQuestionDetailAnswer, TQuestionDifficulty } from '.';
import APIDataProvider, {
  IApiPaginationData,
} from '../../dataProvider/APIDataProvider';
import { AdminApiPaths } from '../../dataProvider/APIPaths';

const QuestionAdminApiDataProvider = new APIDataProvider<IQuestion>();
const QuestionListAdminApiDataProvider = new APIDataProvider<
  IApiPaginationData<IQuestion>
>();

export interface IAdminGetQuestionList {
  limit: number;
  offset: number;
  subTopicIds: Array<string>;
}

export interface IAdminGetQuestions {
  limit: number;
  offset: number;
  questionId?: string;
}

export interface IAdminCreateQuestion {
  subTopics: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  marks?: number;
  tags?: string[];
  details: string;
  markingSchema: string;
  answers: IQuestionDetailAnswer[];
}
export interface IAdminUpdateQuestion {
  id: string;
  subTopics: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  marks?: number;
  tags?: string[];
  details: string;
  markingSchema: string;
  answers: IQuestionDetailAnswer[];
}
export interface IAdminApproveQuestion {
  id: string;
  isApproved: boolean;
}

const adminGetQuestionListModule = async (data: IAdminGetQuestionList) => {
  const response = await QuestionListAdminApiDataProvider.create(
    `${AdminApiPaths.questions}/list`,
    data
  );

  return response.data as {
    limit: number;
    offset: number;
    totalDocs: number;
    docs: Array<IQuestion>;
  };
};

const adminGetQuestionsModule = async (data: IAdminGetQuestions) => {
  let path = `${AdminApiPaths.questions}/allForAdmin?limit=${data.limit}&offset=${data.offset}`;

  if (data.questionId) {
    path += `&questionId=${data.questionId}`;
  }

  const response = await QuestionAdminApiDataProvider.get(path);

  return response.data as {
    limit: number;
    offset: number;
    totalDocs: number;
    docs: Array<IQuestion>;
  };
};

export const adminGetQuestionList = createAsyncThunk(
  'admin/question/list',
  adminGetQuestionListModule
);

export const adminLoadMoreQuestionList = createAsyncThunk(
  'admin/question/loadMore',
  adminGetQuestionListModule
);

export const adminGetQuestions = createAsyncThunk(
  'admin/questions/all',
  adminGetQuestionsModule
);

export const adminLoadMoreQuestions = createAsyncThunk(
  'admin/questions/all/loadMore',
  adminGetQuestionsModule
);

export const adminGetQuestionById = createAsyncThunk(
  'admin/question/id',
  async (id: string) => {
    const response = await QuestionAdminApiDataProvider.getById(
      `${AdminApiPaths.questions}/${id}`
    );

    return response.data;
  }
);

export const adminCreateNewQuestion = createAsyncThunk(
  'admin/question/create',
  async (data: IAdminCreateQuestion) => {
    const response = await QuestionAdminApiDataProvider.create(
      AdminApiPaths.questions,
      data
    );
    return response.data;
  }
);

export const adminUpdateQuestion = createAsyncThunk(
  'admin/question/update',
  async (data: IAdminUpdateQuestion) => {
    const { id, ...apiData } = data;
    const response = await QuestionAdminApiDataProvider.updateById(
      `${AdminApiPaths.questions}/${id}`,
      apiData
    );
    return response.data;
  }
);
export const adminApproveQuestion = createAsyncThunk(
  'admin/question/approve',
  async (data: IAdminApproveQuestion) => {
    const { id, ...apiData } = data;
    const response = await QuestionAdminApiDataProvider.updateById(
      `${AdminApiPaths.questions}/approve/${id}`,
      apiData
    );
    return response.data;
  }
);

export const adminDeleteQuestion = createAsyncThunk(
  'admin/question/delete',
  async (data: { id: string }) => {
    const response = await QuestionAdminApiDataProvider.updateById(
      `${AdminApiPaths.questions}/delete/${data.id}`,
      data
    );
    return response.data;
  }
);
